/**
 * Chat API Service
 * Phase 6.5 - Chat Conversationnel (CFO Virtuel)
 */

import { fetchWithAuth, safeJsonParse } from '../../../utils/api';
import type {
  ChatConversation,
  ConversationWithMessages,
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
  FeedbackRequest,
  ChatStats,
} from '../types/chat.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const CHAT_BASE_URL = `${API_URL}/chat`;

// Helper pour parser les erreurs API
const parseApiError = (errorData: any): string => {
  if (errorData.error && typeof errorData.error === 'object') {
    const err = errorData.error;
    if (err.details && Array.isArray(err.details) && err.details.length > 0) {
      return err.details.map((d: any) => d.message).filter(Boolean).join(', ') || err.message || 'Une erreur est survenue';
    }
    return err.message || 'Une erreur est survenue';
  }
  return errorData.message || errorData.error || 'Une erreur est survenue';
};

// Helper pour les requetes fetch
const fetchApi = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetchWithAuth(url, {
    ...options,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await safeJsonParse(response).catch(() => ({}));
    throw new Error(parseApiError(errorData));
  }

  const json = await safeJsonParse(response);
  return json.data !== undefined ? json.data : json;
};

export const chatService = {
  /**
   * Create a new conversation
   */
  async createConversation(): Promise<ChatConversation> {
    return fetchApi<ChatConversation>(`${CHAT_BASE_URL}/conversations`, {
      method: 'POST',
    });
  },

  /**
   * List all conversations for current user
   */
  async listConversations(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ conversations: ChatConversation[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const queryString = searchParams.toString();
    const url = queryString
      ? `${CHAT_BASE_URL}/conversations?${queryString}`
      : `${CHAT_BASE_URL}/conversations`;

    return fetchApi<{ conversations: ChatConversation[]; total: number }>(url);
  },

  /**
   * Get a conversation with its messages
   */
  async getConversation(id: string): Promise<ConversationWithMessages> {
    return fetchApi<ConversationWithMessages>(
      `${CHAT_BASE_URL}/conversations/${id}`
    );
  },

  /**
   * Get or create active conversation
   */
  async getActiveConversation(): Promise<ConversationWithMessages | null> {
    return fetchApi<ConversationWithMessages | null>(`${CHAT_BASE_URL}/active`);
  },

  /**
   * Close a conversation
   */
  async closeConversation(id: string): Promise<ChatConversation> {
    return fetchApi<ChatConversation>(
      `${CHAT_BASE_URL}/conversations/${id}/close`,
      { method: 'POST' }
    );
  },

  /**
   * Send a message and get AI response
   */
  async sendMessage(request: SendMessageRequest): Promise<SendMessageResponse> {
    const response = await fetchWithAuth(`${CHAT_BASE_URL}/messages`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await safeJsonParse(response).catch(() => ({}));
      return {
        success: false,
        error: parseApiError(errorData),
      };
    }

    const json = await safeJsonParse(response);
    return json;
  },

  /**
   * Submit feedback for a message
   */
  async submitFeedback(
    messageId: string,
    feedback: FeedbackRequest
  ): Promise<ChatMessage> {
    return fetchApi<ChatMessage>(
      `${CHAT_BASE_URL}/messages/${messageId}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify(feedback),
      }
    );
  },

  /**
   * Get chat statistics
   */
  async getStats(): Promise<ChatStats> {
    return fetchApi<ChatStats>(`${CHAT_BASE_URL}/stats`);
  },
};

export default chatService;
